import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Upload, Link, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useJobIntelProfile } from "@/hooks/useJobIntelProfile";
import { useJobIntelCredits } from "@/hooks/useJobIntelCredits";
import { ReportVersionSelector, type ReportVersion } from "@/components/ReportVersionSelector";
import { ResumeMatchResults } from "@/components/ResumeMatchResults";
import InsufficientCreditsModal from "@/components/InsufficientCreditsModal";
import { findOrCreateSavedJob, linkReportToSavedJob } from "@/services/jobintelSavedJobsService";

const PROGRESS_STAGES = [
  { percent: 10, message: "Uploading your resume..." },
  { percent: 25, message: "Extracting resume content..." },
  { percent: 40, message: "Parsing job description..." },
  { percent: 55, message: "Analyzing keyword matches..." },
  { percent: 70, message: "Calculating skills alignment..." },
  { percent: 85, message: "Generating improvement tips..." },
  { percent: 95, message: "Finalizing your match report..." },
  { percent: 100, message: "Analysis complete!" },
];

export function ResumeJobMatchSearch() {
  const { user } = useAuth();
  const { profile } = useJobIntelProfile();
  const { checkAndDeductCredits, refundCredits, access } = useJobIntelCredits();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [jobUrl, setJobUrl] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [reportVersion, setReportVersion] = useState<ReportVersion>('snapshot');
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [savedJobId, setSavedJobId] = useState<string | undefined>();
  const [resumeStoragePath, setResumeStoragePath] = useState<string>('');
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [creditCost, setCreditCost] = useState(3);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF or DOCX file");
        return;
      }
      setResumeFile(file);
      toast.success(`Resume uploaded: ${file.name}`);
    }
  };

  const simulateProgress = async () => {
    for (let i = 0; i < PROGRESS_STAGES.length - 1; i++) {
      const stage = PROGRESS_STAGES[i];
      setProgress(stage.percent);
      setProgressMessage(stage.message);
      await new Promise(resolve => setTimeout(resolve, 3000 + Math.random() * 5000));
    }
  };

  const handleGenerate = async () => {
    if (!resumeFile) {
      toast.error("Please upload your resume");
      return;
    }
    if (!jobUrl && !jobDescription) {
      toast.error("Please provide a job URL or paste the job description");
      return;
    }
    if (!user) {
      toast.error("Please sign in to generate reports");
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setProgressMessage("Starting analysis...");

    let transactionId: string | undefined;

    try {
      // Step 1: Check and deduct credits
      const creditResult = await checkAndDeductCredits('resume_job_match', reportVersion);
      if (!creditResult.success) {
        if (creditResult.error_code === 'INSUFFICIENT_CREDITS') {
          setCreditCost(creditResult.cost || 3);
          setShowInsufficientCredits(true);
        } else {
          toast.error(creditResult.message || 'Credit check failed');
        }
        setIsGenerating(false);
        return;
      }
      transactionId = creditResult.transaction_id;

      // Start progress simulation
      const progressPromise = simulateProgress();

      // Step 2: Upload resume to storage
      const fileExt = resumeFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}_resume.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('uploaded-documents')
        .upload(fileName, resumeFile);

      if (uploadError) throw new Error(`Failed to upload resume: ${uploadError.message}`);
      setResumeStoragePath(fileName);

      // Step 3: Extract resume text
      const { data: { publicUrl } } = supabase.storage
        .from('uploaded-documents')
        .getPublicUrl(fileName);

      const { data: extractData, error: extractError } = await supabase.functions.invoke('extract-pdf-text', {
        body: { fileUrl: publicUrl, fileName: resumeFile.name }
      });

      if (extractError || !extractData?.text) {
        throw new Error('Failed to extract resume text');
      }

      const resumeText = extractData.text;
      const jobDescText = jobDescription || `Job URL: ${jobUrl}`;

      // Step 4: Check for existing Job Insider Report
      let existingJobInsiderData = null;
      try {
        const { data: insiderReports } = await supabase
          .from('job_intel_reports' as any)
          .select('report')
          .eq('user_id', user.id)
          .ilike('company_name', `%${jobDescription.split('\n')[0]?.trim() || ''}%`)
          .limit(1);

        if (insiderReports?.length) {
          existingJobInsiderData = (insiderReports[0] as any).report;
        }
      } catch { /* no insider data available */ }

      // Step 5: Call edge function
      const { data, error } = await supabase.functions.invoke('analyze-resume-match', {
        body: {
          action: 'resume_job_match',
          resumeText,
          job_description_text: jobDescText,
          user_profile: profile || null,
          report_version: reportVersion,
          report_context: 'job_match',
          existing_job_insider_data: existingJobInsiderData,
        }
      });

      if (error || !data?.success) {
        if (transactionId) await refundCredits(transactionId);
        throw new Error(data?.error || error?.message || 'Analysis failed');
      }

      const result = data.result;

      // Wait for progress animation
      await progressPromise;
      setProgress(100);
      setProgressMessage("Analysis complete!");

      // Step 6: Auto-save job
      const companyName = result.company_problems_coverage?.[0]?.problem_title ? 'Unknown Company' : 'Unknown Company';
      const jobTitle = 'Target Position';

      const savedJob = await findOrCreateSavedJob(
        user.id,
        jobTitle,
        companyName,
        jobUrl || undefined,
        jobDescText,
        'resume_match'
      );

      // Step 7: Save result to DB
      const { data: resultRecord, error: saveError } = await supabase
        .from('jobintel_resume_match_results' as any)
        .insert({
          user_id: user.id,
          resume_filename: resumeFile.name,
          resume_storage_path: fileName,
          job_title: jobTitle,
          company_name: companyName,
          job_description_text: jobDescText,
          job_url: jobUrl || null,
          report_version: reportVersion,
          ats_match_score: result.ats_match_score?.score,
          human_eye_score: result.human_eye_score?.score,
          overall_match_score: Math.round(((result.ats_match_score?.score || 0) + (result.human_eye_score?.score || 0)) / 2),
          tailoring_verdict: result.tailoring_verdict?.verdict,
          result_json: result,
          credit_transaction_id: transactionId,
          saved_job_id: savedJob.id,
        } as any)
        .select()
        .single();

      if (!saveError && resultRecord) {
        await linkReportToSavedJob(savedJob.id, 'resume_job_match', reportVersion, (resultRecord as any).id);
      }

      setSavedJobId(savedJob.id);
      setAnalysisResult(result);
      toast.success("Match analysis complete!");

    } catch (error: any) {
      console.error("Error generating match report:", error);
      toast.error(error.message || "Failed to generate report");
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setProgressMessage("");
    }
  };

  // Show results if we have them
  if (analysisResult) {
    return (
      <ResumeMatchResults
        result={analysisResult}
        savedJobId={savedJobId}
        jobDescriptionText={jobDescription}
        resumeStoragePath={resumeStoragePath}
      />
    );
  }

  if (isGenerating) {
    return (
      <Card className="border-2 border-green-300 bg-white shadow-lg">
        <CardContent className="py-12">
          <div className="max-w-md mx-auto space-y-6 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Analyzing Your Match</h3>
              <p className="text-muted-foreground">{progressMessage}</p>
            </div>
            <Progress value={progress} className="h-3" />
            <p className="text-sm text-muted-foreground">This typically takes 30-60 seconds...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-2 border-green-300 bg-white shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Generate Match Analysis</CardTitle>
          <CardDescription>Upload your resume and provide a job posting to analyze</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Resume Upload */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Your Resume <span className="text-red-500">*</span></Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-green-400 hover:bg-green-50/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" accept=".pdf,.docx" onChange={handleFileChange} className="hidden" />
              {resumeFile ? (
                <div className="flex items-center justify-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <span className="font-medium text-green-700">{resumeFile.name}</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-sm text-muted-foreground">Click to upload PDF or DOCX</p>
                </div>
              )}
            </div>
          </div>

          {/* Job URL */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Job Posting URL</Label>
            <div className="relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={jobUrl} onChange={e => setJobUrl(e.target.value)} placeholder="https://linkedin.com/jobs/..." className="pl-10 border-2 border-border" />
            </div>
          </div>

          {/* OR Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <span className="text-sm text-muted-foreground font-medium">OR</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Job Description Text */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Paste Job Description</Label>
            <Textarea value={jobDescription} onChange={e => setJobDescription(e.target.value)} placeholder="Paste the full job description here..." className="min-h-[150px] border-2 border-border" />
          </div>

          {/* Report Version Selector */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Report Depth</Label>
            <ReportVersionSelector value={reportVersion} onChange={setReportVersion} />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleGenerate}
            disabled={!resumeFile || (!jobUrl && !jobDescription)}
            className="w-full bg-gradient-to-r from-green-500 to-lime-500 hover:from-green-600 hover:to-lime-600 text-white py-6 text-lg font-semibold"
          >
            <FileText className="w-5 h-5 mr-2" /> Generate Match Analysis
          </Button>

          <p className="text-sm text-center text-muted-foreground">Your data is processed securely and never shared with third parties.</p>
        </CardContent>
      </Card>

      <InsufficientCreditsModal
        open={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        currentBalance={access?.credits_remaining || 0}
        requiredCost={creditCost}
      />
    </>
  );
}
